import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { TagModule } from 'primeng/tag';
import { HttpParams } from '@angular/common/http';
import { ToastModule } from 'primeng/toast';

import { Notification } from './notification.model';
import { NotificationService } from './notification.service';
import { AuthenticationService } from '../../services/authentication.service';
import { AppUser } from '../../users/user.model';
import { MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AppDatePipe } from '../../pipes/app-date.pipe';

@Component({
    selector: 'app-notification-list',
    standalone: true,
    imports: [CommonModule, ButtonModule, DialogModule, BadgeModule, 
        AvatarModule, TagModule, RouterModule, ToastModule,
        ProgressSpinnerModule, AppDatePipe],
    providers: [MessageService],
    templateUrl: './notification-list.component.html',
    styleUrls: ['./notification-list.component.css']
})
export class NotificationListComponent implements OnInit {

    @Input() display: 'inline' | 'dialog' = 'dialog';
    visible: boolean = false;
    notifications: Notification[] = [];
    loading = false;
    currentUser: AppUser | null = null;

    constructor(
        private router: Router,
        private notificationService: NotificationService,
        private authService: AuthenticationService,
        private messageService: MessageService
    ) { }

    ngOnInit() {
        this.authService.currentUser$.subscribe(user => {
            this.currentUser = user;
            if (user) {
                this.loadNotifications();
            }
        });
    }

    loadNotifications() {
        this.loading = true;
        const params = new HttpParams()
            .set('sort', 'createdAt,desc')
            .set('size', '20'); // Get latest 20

        this.notificationService.getNotifications(params).subscribe({
            next: (page) => {
                this.notifications = page.content;
                this.loading = false;
            },
            error: (err) => {
                console.error('Error loading notifications', err);
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load notifications.' });
            }
        });
    }

    showDialog() {
        this.visible = true;
        this.loadNotifications(); // Refresh when opening
    }

    markAsRead(notification: Notification, event: Event) {
        event.stopPropagation(); // Prevent click from propagating to the parent element
        if (notification.read) return;

        this.notificationService.markAsRead(notification.id).subscribe({
            next: (updatedNotification) => {
                const index = this.notifications.findIndex(n => n.id === notification.id);
                if (index > -1) {
                    this.notifications[index] = updatedNotification;
                }
            },
            error: (err) => {
                console.error('Error marking notification as read', err);
            }
        });
    }

    markAllAsRead() {
        this.notificationService.markAllAsRead().subscribe({
            next: () => {
                this.notifications.forEach(n => n.read = true);
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'All notifications marked as read.' });
            },
            error: (err) => {
                console.error('Error marking all as read', err);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to mark all as read.' });
            }
        });
    }

    onNotificationClick(notification: Notification): void {
        if (!notification.read) {
            this.markAsRead(notification, new Event('click'));
        }
        this.visible = false; // Close dialog on click
        if (notification.link) {
            this.router.navigateByUrl(notification.link);
        }
    }

    getSeverity(notification: Notification): string {
        return notification.severity || 'info';
    }

    getUnreadCount(): number {
        return this.notifications.filter(notification => !notification.read).length;
    }
}
